import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UnitCountTwo from "./Component/UnitCountTwo";
import RevenueGrowthOne from "./Component/RevenueGrowthOne";
import EarningStaticOne from "./Component/EarningStaticOne";
import CampaignStaticOne from "./Component/CampaignStaticOne";
import LatestPerformanceOne from "./Component/LatestPerformanceOne";
import LastTransactionOne from "./Component/LastTransactionOne";
import AllComplaintsSection from "./Component/AllComplaintsSection";
import { getWebmasterDashboard } from "../../../redux/actions/SocietyController/CtrlDashboardAction/CtrlDashboardAction";
import { getOperationsDashboard } from "../../../redux/actions/SocietyPortal/DashboardAction/DashboardAction";
import { getSocietyDropdown } from "../../../redux/actions/SocietyController/CtrlEmployeeAction/CtrlEmployeeAction";
import OperationsSection from "../../../components/OperationsSection";
import SearchableDropdown from "../../../components/SearchableDropdown";

const Dashboard = () => {
    const dispatch = useDispatch();
    const { data, loading } = useSelector((s) => s.CtrlDashboardReducer || {});
    const {
        operations: opsData,
        operationsLoading: opsLoading,
        operationsError: opsError
    } = useSelector((s) => s.DashboardReducer || {});
    const { societyDropdownData, societyDropdownLoading } = useSelector((s) => s.CtrlEmployeeReducer || {});

    const [loginFrequency, setLoginFrequency] = useState('monthly');
    const [userTypeFrequency, setUserTypeFrequency] = useState('monthly');
    // '' = All societies (cross-society aggregate). Value is stringified because
    // <select> options serialize to strings; the request request-builder coerces
    // to number when non-empty.
    const [selectedSociety, setSelectedSociety] = useState('');

    useEffect(() => {
        dispatch(getWebmasterDashboard({ loginFrequency, userTypeFrequency }));
        dispatch(getSocietyDropdown());
    }, [dispatch, loginFrequency, userTypeFrequency]);

    // Pass explicit 'all' when the dropdown is empty so the request-builder
    // does NOT fall back to the caller's storer-selected society (that
    // fallback is only wanted for the SocietyPortal page).
    const loadOperations = useCallback(() => {
        dispatch(getOperationsDashboard({
            society_id: selectedSociety ? Number(selectedSociety) : 'all'
        }));
    }, [dispatch, selectedSociety]);

    useEffect(() => { loadOperations(); }, [loadOperations]);

    const societyOptions = useMemo(() => {
        // getSocietyDropdown returns either an array or {data:[...]}. The
        // reducer already normalizes to an array.
        const list = Array.isArray(societyDropdownData) ? societyDropdownData : [];
        return list.map(s => ({
            value: String(s.society_id ?? s.id),
            label: s.name || s.society_name || `Society #${s.society_id ?? s.id}`
        }));
    }, [societyDropdownData]);

    const filterSlot = (
        <div className="tw-flex tw-items-center tw-gap-2">
            <label className="tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-0">
                Society
            </label>
            <div className="tw-w-[220px]">
                {/* Clearing hands back 'all' — that is the cross-society aggregate */}
                <SearchableDropdown
                    small
                    value={selectedSociety}
                    onChange={(v) => setSelectedSociety(v === 'all' ? '' : v)}
                    options={societyOptions}
                    loading={societyDropdownLoading}
                    placeholder="All societies"
                />
            </div>
        </div>
    );

    return (
        <section className="row gy-4">
            <OperationsSection
                data={opsData}
                loading={opsLoading}
                error={opsError}
                onRetry={loadOperations}
                filterSlot={filterSlot}
            />
            {/* Follows the same society filter as the Operations Overview above */}
            <AllComplaintsSection societyId={selectedSociety} />
            <UnitCountTwo tiles={data?.tiles} loading={loading} />
            <RevenueGrowthOne societyIncrement={data?.societyIncrement} />
            <EarningStaticOne
                loginStatistics={data?.loginStatistics}
                frequency={loginFrequency}
                onFrequencyChange={setLoginFrequency}
            />
            <CampaignStaticOne
                userTypes={data?.userTypes}
                frequency={userTypeFrequency}
                onFrequencyChange={setUserTypeFrequency}
            />
            <LatestPerformanceOne
                recentSocieties={data?.recentSocieties}
                recentUsers={data?.recentUsers}
            />
            <LastTransactionOne recentActivity={data?.recentActivity} />
        </section>
    );
};

export { Dashboard };
